/**
 * AccessProposalCard — a chat-emitted proposal the lead confirms with ONE tap.
 *
 * Mirrors the allocation board's ghost-proposal pattern: the board is the
 * agent's face; chat is an optional side-channel. When the agent proposes a
 * grant / revoke / approval, it renders as this card — labelled, never
 * auto-executed. The lead taps Confirm (the only place an access change fires)
 * or dismisses it. Execution is the caller's job via `onConfirm`; this component
 * only signals intent.
 */

import { useTranslation } from 'react-i18next'
import { Check, UserRoundMinus, UserRoundPlus, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { ExpiryChip, RoleBadge } from '@frontend/components/access/atoms'
import { resourceIcon } from '@frontend/components/access/accessMeta'
import { PersonAvatar } from '@frontend/components/access/PersonAvatar'
import type { AccessProposal, AccessProposalAction } from '@frontend/components/access/types'

const ACTION_ICON: Record<AccessProposalAction, LucideIcon> = {
  grant: UserRoundPlus,
  revoke: UserRoundMinus,
  approve: Check,
}

export interface AccessProposalCardProps {
  proposal: AccessProposal
  onConfirm: (proposal: AccessProposal) => void
  onDismiss: (proposal: AccessProposal) => void
  pending?: boolean
  now?: number
  className?: string
}

export function AccessProposalCard({
  proposal,
  onConfirm,
  onDismiss,
  pending,
  now,
  className,
}: AccessProposalCardProps) {
  const { t } = useTranslation()
  const ActionIcon = ACTION_ICON[proposal.action]
  const ResourceIcon = resourceIcon(proposal.resource.type)
  // Revoke is subtractive — a muted destructive-leaning confirm, never the loud
  // Action-Blue, so a fast tap can't casually strip access.
  const isRevoke = proposal.action === 'revoke'

  return (
    <div
      className={cn(
        'rounded-[18px] border border-primary-100 bg-primary-50/60 p-4',
        className,
      )}
    >
      <div className="flex items-center gap-2">
        <span
          className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-white text-primary"
          aria-hidden="true"
        >
          <ActionIcon className="h-4 w-4" />
        </span>
        <p className="text-[13px] font-semibold uppercase tracking-wide text-primary-700">
          {t(`access.proposal.action.${proposal.action}`)}
        </p>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5">
          <PersonAvatar user={proposal.subject} size="sm" />
          <span className="text-[15px] font-semibold text-neutral-900">
            {proposal.subject.name}
          </span>
        </span>
        <span className="text-neutral-400" aria-hidden="true">
          ·
        </span>
        <span className="inline-flex min-w-0 items-center gap-1.5">
          <ResourceIcon className="h-4 w-4 shrink-0 text-neutral-400" aria-hidden="true" />
          <span className="truncate text-[15px] text-neutral-800">{proposal.resource.label}</span>
        </span>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <RoleBadge role={proposal.role} />
        {proposal.expires_at && <ExpiryChip expiresAt={proposal.expires_at} now={now} />}
      </div>

      {proposal.rationale && (
        <p className="mt-3 text-[13px] leading-relaxed text-neutral-600">{proposal.rationale}</p>
      )}

      <div className="mt-4 flex gap-2">
        <Button
          type="button"
          size="pill"
          variant={isRevoke ? 'outline' : 'default'}
          disabled={pending}
          onClick={() => onConfirm(proposal)}
          className={cn(
            'min-h-11',
            isRevoke &&
              'border-destructive/30 text-destructive hover:border-destructive/50 hover:bg-destructive/5 hover:text-destructive',
          )}
        >
          {pending
            ? t('access.proposal.confirming')
            : t(`access.proposal.confirm.${proposal.action}`)}
        </Button>
        <Button
          type="button"
          size="pill"
          variant="ghost"
          disabled={pending}
          onClick={() => onDismiss(proposal)}
          className="min-h-11"
        >
          {t('access.proposal.dismiss')}
        </Button>
      </div>
    </div>
  )
}
